import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { knowledgeApi } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Plus, Pencil, Trash2, Eye, EyeOff, Star, StarOff,
  Loader2, FileText, Save, X, GripVertical
} from 'lucide-react';

const CATEGORY_OPTIONS = [
  { value: 'personal', label: 'För personal' },
  { value: 'skolledning', label: 'För skolledning' },
  { value: 'it', label: 'För IT' },
  { value: 'vardnadshavare', label: 'För vårdnadshavare & elever' },
  { value: 'skyltar', label: 'Skyltar & mallar' },
];

const DOC_TYPE_OPTIONS = [
  { value: 'guide', label: 'Guide' },
  { value: 'mall', label: 'Mall' },
  { value: 'skylt', label: 'Skylt' },
  { value: 'faq', label: 'FAQ' },
  { value: 'info', label: 'Information' },
  { value: 'presentation', label: 'Presentation' },
];

const ROLE_OPTIONS = [
  { value: 'superadmin', label: 'Superadmin' },
  { value: 'kommun_admin', label: 'Kommunadmin' },
  { value: 'school_admin', label: 'Skoladmin' },
  { value: 'staff', label: 'Personal' },
];

export default function KnowledgeAdminPage() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDoc, setEditingDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadDocuments(); }, []);

  const loadDocuments = async () => {
    try {
      const res = await knowledgeApi.adminDocuments();
      setDocuments(res.data.documents || []);
    } catch {
      toast.error('Kunde inte ladda dokument');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (doc) => {
    try {
      await knowledgeApi.updateDocument(doc.id, { is_published: !doc.is_published });
      toast.success(doc.is_published ? 'Avpublicerad' : 'Publicerad');
      loadDocuments();
    } catch {
      toast.error('Kunde inte uppdatera');
    }
  };

  const handleToggleRecommended = async (doc) => {
    try {
      await knowledgeApi.updateDocument(doc.id, { is_recommended: !doc.is_recommended });
      toast.success(doc.is_recommended ? 'Borttagen fran rekommenderade' : 'Markerad som rekommenderad');
      loadDocuments();
    } catch {
      toast.error('Kunde inte uppdatera');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Ta bort "${doc.title}"?`)) return;
    try {
      await knowledgeApi.deleteDocument(doc.id);
      toast.success('Dokumentet borttaget');
      loadDocuments();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  const handleSave = async () => {
    if (!editingDoc) return;
    setSaving(true);
    try {
      if (editingDoc.id) {
        await knowledgeApi.updateDocument(editingDoc.id, editingDoc);
        toast.success('Dokumentet uppdaterat');
      } else {
        await knowledgeApi.createDocument(editingDoc);
        toast.success('Nytt dokument skapat');
      }
      setEditingDoc(null);
      loadDocuments();
    } catch {
      toast.error('Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const handleNewDocument = () => {
    setEditingDoc({
      title: '',
      slug: '',
      description: '',
      category: 'personal',
      doc_type: 'guide',
      content: '',
      roles: ['superadmin'],
      is_published: false,
      is_recommended: false,
      version: '1.0',
      badges: [],
      has_pdf: false,
      pdf_endpoint: '',
      pdf_type: 'static',
      template_fields: [],
      sort_order: documents.length + 1,
    });
  };

  const updateField = (field, value) => {
    setEditingDoc(prev => ({ ...prev, [field]: value }));
  };

  const toggleRole = (role) => {
    setEditingDoc(prev => {
      const roles = prev.roles || [];
      return { ...prev, roles: roles.includes(role) ? roles.filter(r => r !== role) : [...roles, role] };
    });
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="knowledge-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/knowledge')} className="gap-1.5 text-slate-500 -ml-2 mb-2" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" /> Tillbaka
          </Button>
          <h1 className="font-chivo font-black text-2xl text-navy dark:text-white tracking-tight">
            Hantera kunskapsbank
          </h1>
          <p className="text-slate-500 dark:text-slate-400 font-ibm text-sm mt-1">
            Skapa, redigera och hantera dokument i kunskapsbanken
          </p>
        </div>
        <Button onClick={handleNewDocument} className="gap-2" data-testid="new-document-btn">
          <Plus className="h-4 w-4" /> Nytt dokument
        </Button>
      </div>

      {/* Edit form */}
      {editingDoc && (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-chivo font-bold text-lg text-navy dark:text-white">
                {editingDoc.id ? 'Redigera dokument' : 'Nytt dokument'}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setEditingDoc(null)}><X className="h-4 w-4" /></Button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="font-ibm text-xs">Titel</Label>
                <Input value={editingDoc.title} onChange={e => updateField('title', e.target.value)} className="mt-1" data-testid="edit-title" />
              </div>
              <div>
                <Label className="font-ibm text-xs">Slug (URL)</Label>
                <Input value={editingDoc.slug} onChange={e => updateField('slug', e.target.value)} className="mt-1" data-testid="edit-slug" />
              </div>
              <div className="sm:col-span-2">
                <Label className="font-ibm text-xs">Beskrivning</Label>
                <Textarea value={editingDoc.description} onChange={e => updateField('description', e.target.value)} className="mt-1" rows={2} data-testid="edit-description" />
              </div>
              <div>
                <Label className="font-ibm text-xs">Kategori</Label>
                <select value={editingDoc.category} onChange={e => updateField('category', e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm" data-testid="edit-category">
                  {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="font-ibm text-xs">Dokumenttyp</Label>
                <select value={editingDoc.doc_type} onChange={e => updateField('doc_type', e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm" data-testid="edit-doc-type">
                  {DOC_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="font-ibm text-xs">Version</Label>
                <Input value={editingDoc.version} onChange={e => updateField('version', e.target.value)} className="mt-1" data-testid="edit-version" />
              </div>
              <div>
                <Label className="font-ibm text-xs">Sorteringsordning</Label>
                <Input type="number" value={editingDoc.sort_order} onChange={e => updateField('sort_order', parseInt(e.target.value) || 0)} className="mt-1" data-testid="edit-sort-order" />
              </div>
            </div>

            <div>
              <Label className="font-ibm text-xs mb-2 block">Synlig for roller</Label>
              <div className="flex flex-wrap gap-2">
                {ROLE_OPTIONS.map(r => (
                  <button
                    key={r.value}
                    onClick={() => toggleRole(r.value)}
                    className={`px-3 py-1.5 rounded-md text-xs font-ibm font-medium border transition-colors ${
                      (editingDoc.roles || []).includes(r.value)
                        ? 'bg-navy text-white border-navy'
                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600'
                    }`}
                    data-testid={`role-toggle-${r.value}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm font-ibm">
                <input type="checkbox" checked={editingDoc.is_published} onChange={e => updateField('is_published', e.target.checked)} className="rounded" />
                Publicerad
              </label>
              <label className="flex items-center gap-2 text-sm font-ibm">
                <input type="checkbox" checked={editingDoc.is_recommended} onChange={e => updateField('is_recommended', e.target.checked)} className="rounded" />
                Rekommenderad
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditingDoc(null)}>Avbryt</Button>
              <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-document-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Spara
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document list */}
      <div className="space-y-2">
        {documents.map(doc => (
          <Card key={doc.id} className={`border-slate-200 dark:border-slate-700 ${!doc.is_published ? 'opacity-60' : ''}`} data-testid={`admin-doc-${doc.slug}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <GripVertical className="h-4 w-4 text-slate-300 shrink-0 hidden sm:block" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-ibm font-semibold text-sm text-navy dark:text-white truncate">{doc.title}</p>
                  {!doc.is_published && <Badge variant="secondary" className="text-[10px]">Utkast</Badge>}
                  {doc.is_recommended && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-ibm truncate">{doc.category} &middot; v{doc.version}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleToggleRecommended(doc)} title={doc.is_recommended ? 'Ta bort rekommendation' : 'Rekommendera'}>
                  {doc.is_recommended ? <StarOff className="h-4 w-4 text-amber-500" /> : <Star className="h-4 w-4 text-slate-400" />}
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleTogglePublish(doc)} title={doc.is_published ? 'Avpublicera' : 'Publicera'}>
                  {doc.is_published ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => navigate(`/knowledge/${doc.slug}`)} title="Förhandsgranska">
                  <Eye className="h-4 w-4 text-blue-500" />
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setEditingDoc({...doc})} title="Redigera" data-testid={`edit-${doc.slug}-btn`}>
                  <Pencil className="h-4 w-4 text-slate-400" />
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDelete(doc)} title="Ta bort" data-testid={`delete-${doc.slug}-btn`}>
                  <Trash2 className="h-4 w-4 text-red-400" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
